// components/Sidebar2.tsx
import { useEffect, useState } from 'react';
import { IconChevronsLeft, IconMenu2, IconX } from '@tabler/icons-react';
import { Layout, LayoutHeader } from './custom/layout';
import { Button } from './custom/button';
import Nav from './nav';
import { cn } from '@/lib/utils';
import logo from '@/assets/images/logo.png';
import { useSideLinks } from '@/data/sidelinks';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '@/redux/reducer/reducer';
import { fetchRoles } from '@/redux/slice/roleSlice';
import { AppDispatch } from '@/redux/store';

interface SidebarProps extends React.HTMLAttributes<HTMLElement> {
  readonly isCollapsed: boolean;
  readonly setIsCollapsed: React.Dispatch<React.SetStateAction<boolean>>;
}

export default function Sidebar2({
  className,
  isCollapsed,
  setIsCollapsed,
}: SidebarProps) {
  const [navOpened, setNavOpened] = useState(false);
  const { sideLinks, loading, error } = useSideLinks();
  const dispatch = useDispatch<AppDispatch>();
  const { user } = useSelector((state: RootState) => state.auth);
  const { roles } = useSelector((state: RootState) => state.roles);

  useEffect(() => {
    dispatch(fetchRoles());
  }, [dispatch]);

  const roleNames = user?.roles
    ?.map((roleId) => roles?.find((role) => role.id === roleId)?.name)
    ?.filter(Boolean); // remove undefined entries if any
  // Prevent body scroll when the mobile nav is open
  useEffect(() => {
    if (navOpened) {
      document.body.classList.add('overflow-hidden');
    } else {
      document.body.classList.remove('overflow-hidden');
    }
  }, [navOpened]);

  // Determine the dynamic class for the Nav component based on navOpened state
  const navDynamicClasses = navOpened
    ? 'max-h-screen'
    : 'max-h-0 py-0 md:max-h-screen md:py-2';

  // Extract width class for the aside element
  const asideWidthClass = isCollapsed ? 'md:w-14' : 'md:w-64';

  // Extract navigation content to avoid nested ternary operations
  let navContent;
  if (loading) {
    navContent = <div className="p-4">Loading...</div>;
  } else if (error) {
    navContent = <div className="p-4 text-red-500">Error: {error}</div>;
  } else {
    navContent = (
      <Nav
        id="sidebar-menu"
        className={`h-full flex-1 overflow-auto ${navDynamicClasses}`}
        closeNav={() => setNavOpened(false)}
        isCollapsed={isCollapsed}
        links={sideLinks}
      />
    );
  }

  return (
    <aside
      className={cn(
        `fixed left-0 right-0 top-0 z-50 w-full border-r-2 border-r-muted transition-[width] md:bottom-0 md:right-auto md:h-svh ${asideWidthClass}`,
        className
      )}
    >
      {/* Mobile overlay using a button for accessibility */}
      <button
        type="button"
        onClick={() => setNavOpened(false)}
        className={`absolute inset-0 transition-[opacity] delay-100 duration-700 ${
          navOpened ? 'h-svh opacity-50' : 'h-0 opacity-0'
        } w-full bg-black md:hidden`}
        aria-label="Close Navigation Overlay"
      />

      <Layout>
        {/* Header */}
        <LayoutHeader className="sticky top-0 justify-between px-4 py-3 shadow md:px-4">
          <div className={`flex items-center ${!isCollapsed ? 'gap-2' : ''}`}>
            <img
              src={logo}
              alt="Website Logo"
              className={`transition-all ${isCollapsed ? 'h-6 w-6' : 'h-8 w-8'}`}
            />
            <div
              className={`flex flex-col justify-end truncate ${
                isCollapsed ? 'invisible w-0' : 'visible w-auto'
              }`}
            >
              <span className="font-medium">HD FOREX </span>
              <span className="text-xs">{roleNames?.length ? roleNames.join(', ') : 'No roles assigned'}</span>
            </div>
          </div>

          {/* Mobile toggle button */}
          <Button
            variant="ghost"
            size="icon"
            className="md:hidden"
            aria-label="Toggle Navigation"
            aria-controls="sidebar-menu"
            aria-expanded={navOpened}
            onClick={() => setNavOpened((prev) => !prev)}
          >
            {navOpened ? <IconX /> : <IconMenu2 />}
          </Button>
        </LayoutHeader>

        {/* Navigation links */}
        {navContent}

        {/* Collapse toggle button */}
        <Button
          onClick={() => setIsCollapsed((prev) => !prev)}
          size="icon"
          variant="outline"
          className="absolute -right-5 top-1/2 hidden rounded-full md:inline-flex"
        >
          <IconChevronsLeft
            stroke={1.5}
            className={`h-5 w-5 ${isCollapsed ? 'rotate-180' : ''}`}
          />
        </Button>
      </Layout>
    </aside>
  );
}
